import React, { useState, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Box,
    Typography,
    TextField,
    InputAdornment,
    FormControl,
    Select,
    MenuItem,
    SelectChangeEvent,
    InputLabel,
    Grid,
    IconButton,
    Badge,
    Chip,
} from '@mui/material';
import { DialogueSheet } from '../../../DialogueSheet/DialogueSheet';
import {
    Search as SearchIcon,
    Sort as SortIcon,
    Close as CloseIcon,
    EmojiEvents as TrophyIcon,
    Star as MedalIcon,
    Grade as AwardIcon,
    Diamond as GiftIcon,
    Close,
} from '@mui/icons-material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { IAwardCategory, IManageContestSubmissions, TSortBy, TCampSortOrder } from '../../../../redux/contest/types';
import { IContest } from '../../../../types/global/helper';
import CustomButton from '../../../Button/CustomButton';
import { getIdForIbjects } from '../../../../utils/AppUtils';
import { v4 as uuidv4 } from 'uuid';

interface PrizeAward {
    award_category: string;
    award_project: string;
    award_status: 'won' | 'nominated';
    award_year: string;
    person_awarded: string;
    contest: string;
    requestId?: string;
}

interface PrizeAwardModalProps {
    open: boolean;
    onClose: () => void;
    awardCategories: IAwardCategory[];
    submissions: IManageContestSubmissions[];
    contest: IContest;
    existingAwards?: any; // IManagedAwards from the manage response
    onAward: (awards: PrizeAward[]) => void;
}

// Sort options for submissions
const SORT_OPTIONS = [
    { value: 'createdAt_desc', label: 'Oldest First' },
    { value: 'createdAt_asc', label: 'Newest First' },
    { value: 'votes_desc', label: 'Most Votes' },
    { value: 'votes_asc', label: 'Least Votes' },
    { value: 'jury_score_desc', label: 'Highest Jury Score' },
    { value: 'jury_score_asc', label: 'Lowest Jury Score' },
];

const PRIZE_ICONS = {
    trophy: TrophyIcon,
    medal: MedalIcon,
    award: AwardIcon,
    gift: GiftIcon,
};

export const PrizeAwardModal: React.FC<PrizeAwardModalProps> = ({
    open,
    onClose,
    awardCategories,
    submissions,
    contest,
    existingAwards,
    onAward,
}) => {
    const { themeStyle } = useThemeContext();
    const [draggedPrize, setDraggedPrize] = useState<IAwardCategory | null>(null);
    const [awards, setAwards] = useState<PrizeAward[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOption, setSortOption] = useState('createdAt_desc');
    const [sortOrder, setSortOrder] = useState<TCampSortOrder | undefined>();
    const [sortBy, setSortBy] = useState<TSortBy | undefined>();
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [dropTarget, setDropTarget] = useState<string | null>(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [preloadedAwardIds, setPreloadedAwardIds] = useState<Set<string>>(new Set());

    // Preload existing awards when modal opens
    useEffect(() => {
        if (open && existingAwards?.awards) {
            const preloadedAwards: PrizeAward[] = [];
            const preloadedIds = new Set<string>();

            // Flatten all awards from the manage response
            existingAwards.awards.forEach((award: any) => {
                const awardId = award.award_category?._id || award.award_category;
                const projectId = award.award_project?._id || award.award_project;

                preloadedAwards.push({
                    award_category: awardId,
                    award_project: projectId,
                    award_status: award.award_status || 'won',
                    award_year: award.award_year || award.created_at,
                    person_awarded: award.person_awarded?._id || award.person_awarded,
                    contest: award.contest?._id || award.contest,
                    requestId: award.requestId || uuidv4(),
                });

                // Track this as a preloaded award (combination of award category and project)
                preloadedIds.add(`${awardId}-${projectId}`);
            });

            setAwards(preloadedAwards);
            setPreloadedAwardIds(preloadedIds);
        } else if (open) {
            // Reset awards when modal opens without existing awards
            setAwards([]);
            setPreloadedAwardIds(new Set());
        }
    }, [open, existingAwards]);

    // Get unique categories for filter
    const categories = useMemo(() => {
        if (!contest.categories_enabled || !contest.categories) return [];
        return contest.categories.map(cat => cat.category_name);
    }, [contest.categories, contest.categories_enabled]);

    // Filter and sort submissions
    const filteredSubmissions = useMemo(() => {
        let filtered = submissions.filter(submission => {
            const matchesSearch =
                submission.project?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                submission.created_by?.userName?.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesCategory = selectedCategory === 'all' || submission.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });

        // Apply sorting
        if (sortBy && sortOrder) {
            filtered.sort((a, b) => {
                let aValue: any;
                let bValue: any;

                switch (sortBy) {
                    case 'createdAt':
                        aValue = new Date(a.createdAt).getTime();
                        bValue = new Date(b.createdAt).getTime();
                        break;
                    case 'votes':
                        aValue = a.totalVotes || 0;
                        bValue = b.totalVotes || 0;
                        break;
                    case 'jury_score':
                        aValue = a.averageJuryScore || 0;
                        bValue = b.averageJuryScore || 0;
                        break;
                    default:
                        return 0;
                }

                if (sortOrder === 'asc') {
                    return aValue > bValue ? 1 : -1;
                } else {
                    return aValue < bValue ? 1 : -1;
                }
            });
        }

        return filtered;
    }, [submissions, searchQuery, selectedCategory, sortBy, sortOrder]);

    // Check if prize is already awarded (from modal state or existing submissions)
    const isPrizeAwarded = (prizeId: string) => {
        // Check if prize is in modal state
        const inModalState = awards.some(award => award.award_category === prizeId);

        // Check if prize is already awarded to any submission
        const inExistingSubmissions = submissions.some(
            submission =>
                submission.awards && submission.awards.some(award => getIdForIbjects(award.award_category) === prizeId),
        );

        return inModalState || inExistingSubmissions;
    };

    // Get prizes awarded to submission (now returns array for multiple prizes)
    const getSubmissionPrizes = (submissionId: string) => {
        const submissionAwards = awards.filter(a => a.award_project === submissionId);
        return submissionAwards.map(award => awardCategories.find(p => p._id === award.award_category)).filter(Boolean);
    };

    // Check if submission already has awards from the submission's awards field
    const getSubmissionExistingAwards = (submission: IManageContestSubmissions) => {
        if (submission.awards && Array.isArray(submission.awards)) {
            return submission.awards
                .map(award => awardCategories.find(p => p._id === getIdForIbjects(award.award_category)))
                .filter(Boolean);
        }
        return [];
    };

    // Get all prizes for a submission (from both modal state and submission's awards field)
    const getAllSubmissionPrizes = (submission: IManageContestSubmissions) => {
        const modalAwards = getSubmissionPrizes(submission.project._id);
        const existingAwards = getSubmissionExistingAwards(submission);

        // Combine and deduplicate
        const allPrizes = [...modalAwards, ...existingAwards];
        const uniquePrizes = allPrizes.filter(
            (prize, index, self) => index === self.findIndex(p => p?._id === prize?._id),
        );

        return uniquePrizes;
    };

    // Handle drag start
    const handleDragStart = (prize: IAwardCategory) => {
        if (!isPrizeAwarded(prize._id)) {
            setDraggedPrize(prize);
        }
    };

    // Handle drag end
    const handleDragEnd = () => {
        setDraggedPrize(null);
        setDropTarget(null);
    };

    // Handle drag over
    const handleDragOver = (e: React.DragEvent, submissionId: string) => {
        e.preventDefault();
        if (draggedPrize) {
            setDropTarget(submissionId);
        }
    };

    // Handle drag leave
    const handleDragLeave = () => {
        setDropTarget(null);
    };

    // Handle drop
    const handleDrop = (e: React.DragEvent, submission: IManageContestSubmissions) => {
        const { project, created_by } = submission;
        e.preventDefault();
        if (draggedPrize) {
            setAwards([
                ...awards,
                {
                    award_category: draggedPrize._id,
                    award_project: project._id,
                    award_status: 'won',
                    award_year: new Date().toISOString(),
                    person_awarded: created_by._id,
                    contest: getIdForIbjects(contest),
                    requestId: uuidv4(),
                },
            ]);
        }
        setDraggedPrize(null);
        setDropTarget(null);
    };

    // Remove award (only allow removal of new awards, not preloaded ones)
    const removeAward = (submissionId: string, prizeId?: string) => {
        if (prizeId) {
            // Check if this is a preloaded award
            const awardKey = `${prizeId}-${submissionId}`;
            if (preloadedAwardIds.has(awardKey)) {
                // Don't allow removal of preloaded awards
                return;
            }

            // Remove specific prize from submission (only new awards)
            setAwards(
                awards.filter(award => !(award.award_project === submissionId && award.award_category === prizeId)),
            );
        } else {
            // Remove all non-preloaded prizes from submission
            setAwards(
                awards.filter(award => {
                    if (award.award_project !== submissionId) return true;
                    const awardKey = `${award.award_category}-${submissionId}`;
                    return !preloadedAwardIds.has(awardKey); // Keep preloaded awards
                }),
            );
        }
    };

    // Handle sort change
    const handleSortChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setSortOption(value);

        const [field, order] = value.split('_');
        setSortOrder(order as TCampSortOrder);
        setSortBy(field as TSortBy);
    };

    // Handle category change
    const handleCategoryChange = (event: SelectChangeEvent<string>) => {
        setSelectedCategory(event.target.value);
    };

    // Handle submit - show confirmation dialog first
    const handleSubmit = () => {
        setConfirmDialogOpen(true);
    };

    // Handle confirmed submit
    const handleConfirmedSubmit = () => {
        onAward(awards);
        setAwards([]);
        setConfirmDialogOpen(false);
        onClose();
    };

    // Handle cancel confirmation
    const handleCancelConfirmation = () => {
        setConfirmDialogOpen(false);
    };

    // Check if an award is preloaded (cannot be removed)
    const isPreloadedAward = (submissionId: string, prizeId: string) => {
        const awardKey = `${prizeId}-${submissionId}`;
        return preloadedAwardIds.has(awardKey);
    };

    // Get icon for prize
    const getPrizeIcon = (prize: IAwardCategory) => {
        // Default to trophy if no specific icon mapping
        const iconKey = prize.category_title.toLowerCase().includes('gold')
            ? 'trophy'
            : prize.category_title.toLowerCase().includes('silver')
            ? 'medal'
            : prize.category_title.toLowerCase().includes('bronze')
            ? 'award'
            : 'gift';
        return PRIZE_ICONS[iconKey as keyof typeof PRIZE_ICONS] || TrophyIcon;
    };

    // Get color for prize
    const getPrizeColor = (prize: IAwardCategory) => {
        return theme[themeStyle].primary_orange; // Use theme color instead of Tailwind
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: theme[themeStyle].secondaryBg,
                        borderRadius: 2,
                        minHeight: '80vh',
                    },
                }}>
                <DialogTitle
                    sx={{
                        backgroundColor: theme[themeStyle].secondaryBg,
                        color: theme[themeStyle].primary_white,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: '1.5rem',
                        borderBottom: `1px solid ${theme[themeStyle].placeholderGray50}`,
                        p: 3,
                    }}>
                    Award Prizes
                </DialogTitle>

                <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '70vh' }}>
                    <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                        {/* Left Panel - Prizes */}
                        <Box
                            sx={{
                                width: '350px',
                                borderRight: `1px solid ${theme[themeStyle].placeholderGray50}`,
                                backgroundColor: theme[themeStyle].secondaryBg,
                                p: 3,
                                overflowY: 'auto',
                            }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: '1.1rem',
                                    color: theme[themeStyle].primary_white,
                                    mb: 2,
                                }}>
                                Available Prizes
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {awardCategories.map(prize => {
                                    const Icon = getPrizeIcon(prize);
                                    const isAwarded = isPrizeAwarded(prize._id);

                                    return (
                                        <Box
                                            key={prize._id}
                                            draggable={!isAwarded}
                                            onDragStart={() => handleDragStart(prize)}
                                            onDragEnd={handleDragEnd}
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                border: `2px solid ${
                                                    isAwarded
                                                        ? theme[themeStyle].placeholderGray50
                                                        : theme[themeStyle].primary_orange
                                                }`,
                                                backgroundColor: isAwarded
                                                    ? theme[themeStyle].placeholderGray50
                                                    : theme[themeStyle].secondaryBg,
                                                opacity: isAwarded ? 0.5 : 1,
                                                cursor: isAwarded ? 'not-allowed' : 'grab',
                                                transition: 'all 0.2s ease',
                                                '&:hover': !isAwarded
                                                    ? {
                                                          borderColor: theme[themeStyle].highlight,
                                                          boxShadow: `0 4px 8px ${theme[themeStyle].primary_orange}20`,
                                                      }
                                                    : {},
                                                position: 'relative',
                                            }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: 1,
                                                        backgroundColor: isAwarded
                                                            ? theme[themeStyle].placeholderGray50
                                                            : getPrizeColor(prize),
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}>
                                                    <Icon
                                                        sx={{
                                                            color: isAwarded
                                                                ? theme[themeStyle].tag_white
                                                                : theme[themeStyle].primary_white,
                                                            fontSize: 24,
                                                        }}
                                                    />
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            fontSize: 14,
                                                            color: isAwarded
                                                                ? theme[themeStyle].tag_white
                                                                : theme[themeStyle].primary_white,
                                                            mb: 0.5,
                                                        }}>
                                                        {prize.category_title}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 12,
                                                            color: theme[themeStyle].tag_white,
                                                        }}>
                                                        {prize.prize}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {isAwarded && (
                                                <Badge
                                                    badgeContent="Awarded"
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8,
                                                        '& .MuiBadge-badge': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                            color: theme[themeStyle].black,
                                                            fontSize: 10,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>

                        {/* Right Panel - Submissions */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            {/* Search and Filters */}
                            <Box
                                sx={{
                                    p: 3,
                                    borderBottom: `1px solid ${theme[themeStyle].placeholderGray50}`,
                                    backgroundColor: theme[themeStyle].secondaryBg,
                                }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            placeholder="Search by title or username"
                                            value={searchQuery}
                                            onChange={e => setSearchQuery(e.target.value)}
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon sx={{ color: theme[themeStyle].tag_white }} />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: searchQuery ? (
                                                    <InputAdornment position="end">
                                                        <CloseIcon
                                                            sx={{
                                                                color: theme[themeStyle].tag_white,
                                                                cursor: 'pointer',
                                                            }}
                                                            onClick={() => setSearchQuery('')}
                                                        />
                                                    </InputAdornment>
                                                ) : null,
                                                sx: {
                                                    backgroundColor: theme[themeStyle].secondaryBg,
                                                    borderRadius: 2,
                                                    color: theme[themeStyle].primary_white,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme[themeStyle].placeholderGray50,
                                                    },
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        {contest?.categories_enabled && categories.length > 0 && (
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    '& .MuiInputBase-root': {
                                                        backgroundColor: theme[themeStyle].secondaryBg,
                                                        color: theme[themeStyle].primary_white,
                                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                        borderRadius: 2,
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme[themeStyle].placeholderGray50,
                                                    },
                                                    '& .MuiSvgIcon-root': {
                                                        color: theme[themeStyle].primary_white,
                                                    },
                                                }}>
                                                <InputLabel
                                                    id="category-select-label"
                                                    sx={{ color: theme[themeStyle].highlight }}>
                                                    Category
                                                </InputLabel>
                                                <Select
                                                    labelId="category-select-label"
                                                    value={selectedCategory}
                                                    onChange={handleCategoryChange}
                                                    label="Category"
                                                    MenuProps={{
                                                        PaperProps: {
                                                            sx: {
                                                                backgroundColor: theme[themeStyle].gray_text,
                                                                color: theme[themeStyle].primary_white,
                                                                '& .MuiMenuItem-root': {
                                                                    color: theme[themeStyle].primary_white,
                                                                    '&:hover': {
                                                                        backgroundColor:
                                                                            theme[themeStyle].background_gradient.end,
                                                                    },
                                                                    '&.Mui-selected': {
                                                                        backgroundColor:
                                                                            theme[themeStyle].primary_orange,
                                                                        color: theme[themeStyle].black,
                                                                        '&:hover': {
                                                                            backgroundColor:
                                                                                theme[themeStyle].primary_orange,
                                                                        },
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    }}>
                                                    <MenuItem value="all">All Categories</MenuItem>
                                                    {categories.map(category => (
                                                        <MenuItem key={category} value={category}>
                                                            {category}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <FormControl
                                            fullWidth
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    backgroundColor: theme[themeStyle].secondaryBg,
                                                    color: theme[themeStyle].primary_white,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    borderRadius: 2,
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].placeholderGray50,
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: theme[themeStyle].primary_white,
                                                },
                                            }}>
                                            <InputLabel
                                                id="sort-select-label"
                                                sx={{ color: theme[themeStyle].highlight }}>
                                                Sort By
                                            </InputLabel>
                                            <Select
                                                labelId="sort-select-label"
                                                value={sortOption}
                                                onChange={handleSortChange}
                                                label="Sort By"
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].primary_white,
                                                            '& .MuiMenuItem-root': {
                                                                color: theme[themeStyle].primary_white,
                                                                '&:hover': {
                                                                    backgroundColor:
                                                                        theme[themeStyle].background_gradient.end,
                                                                },
                                                                '&.Mui-selected': {
                                                                    backgroundColor: theme[themeStyle].primary_orange,
                                                                    color: theme[themeStyle].black,
                                                                    '&:hover': {
                                                                        backgroundColor:
                                                                            theme[themeStyle].primary_orange,
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    },
                                                }}
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <SortIcon sx={{ color: theme[themeStyle].tag_white }} />
                                                    </InputAdornment>
                                                }>
                                                {SORT_OPTIONS.map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Submissions List */}
                            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {filteredSubmissions.map(submission => {
                                        const submissionPrizes = getAllSubmissionPrizes(submission);
                                        const isDropping = dropTarget === submission._id;

                                        return (
                                            <Box
                                                key={submission._id}
                                                onDragOver={e => handleDragOver(e, submission._id)}
                                                onDragLeave={handleDragLeave}
                                                onDrop={e => handleDrop(e, submission)}
                                                sx={{
                                                    p: 3,
                                                    borderRadius: 2,
                                                    border: `2px solid ${
                                                        isDropping
                                                            ? theme[themeStyle].primary_orange
                                                            : submissionPrizes.length > 0
                                                            ? theme[themeStyle].highlight
                                                            : theme[themeStyle].placeholderGray50
                                                    }`,
                                                    backgroundColor: isDropping
                                                        ? `${theme[themeStyle].primary_orange}10`
                                                        : submissionPrizes.length > 0
                                                        ? `${theme[themeStyle].highlight}10`
                                                        : theme[themeStyle].secondaryBg,
                                                    transition: 'all 0.2s ease',
                                                    transform: isDropping ? 'scale(1.02)' : 'scale(1)',
                                                    position: 'relative',
                                                }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                                    <Box sx={{ position: 'relative' }}>
                                                        <Box
                                                            component="img"
                                                            src={
                                                                submission.project?.feature_image || '/placeholder.svg'
                                                            }
                                                            alt={submission.project?.title}
                                                            sx={{
                                                                width: 60,
                                                                height: 60,
                                                                borderRadius: 1,
                                                                objectFit: 'cover',
                                                            }}
                                                        />
                                                        {submissionPrizes.length > 0 && (
                                                            <Box
                                                                sx={{
                                                                    position: 'absolute',
                                                                    bottom: -4,
                                                                    right: -4,
                                                                    display: 'flex',
                                                                    gap: 0.5,
                                                                }}>
                                                                {submissionPrizes.map((prize, index) => (
                                                                    <Box
                                                                        key={prize?._id || index}
                                                                        sx={{
                                                                            p: 0.5,
                                                                            borderRadius: '50%',
                                                                            backgroundColor: getPrizeColor(prize!),
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                        }}>
                                                                        {(() => {
                                                                            const Icon = getPrizeIcon(prize!);
                                                                            return (
                                                                                <Icon
                                                                                    sx={{
                                                                                        fontSize: 12,
                                                                                        color: theme[themeStyle]
                                                                                            .primary_white,
                                                                                    }}
                                                                                />
                                                                            );
                                                                        })()}
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>

                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                fontSize: 16,
                                                                color: theme[themeStyle].primary_white,
                                                                mb: 0.5,
                                                            }}>
                                                            {submission.project?.title}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 14,
                                                                color: theme[themeStyle].tag_white,
                                                                mb: 1,
                                                            }}>
                                                            by @{submission.created_by?.userName || 'Unknown User'}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                            <Chip
                                                                label={`${submission.totalVotes || 0} votes`}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor:
                                                                        theme[themeStyle].placeholderGray50,
                                                                    color: theme[themeStyle].primary_white,
                                                                    fontSize: 11,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                }}
                                                            />
                                                            <Chip
                                                                label={`${submission.averageJuryScore || 0} jury score`}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor:
                                                                        theme[themeStyle].placeholderGray50,
                                                                    color: theme[themeStyle].primary_white,
                                                                    fontSize: 11,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                }}
                                                            />
                                                            {submission.category && (
                                                                <Chip
                                                                    label={submission.category}
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor:
                                                                            theme[themeStyle].primary_orange,
                                                                        color: theme[themeStyle].black,
                                                                        fontSize: 11,
                                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>

                                                    {submissionPrizes.length > 0 && (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 1,
                                                                minWidth: 200,
                                                            }}>
                                                            {submissionPrizes.map((prize, index) => (
                                                                <Box
                                                                    key={prize?._id || index}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 2,
                                                                    }}>
                                                                    <Box sx={{ textAlign: 'right', flex: 1 }}>
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: 12,
                                                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                                color: theme[themeStyle].primary_white,
                                                                            }}>
                                                                            {prize?.category_title}
                                                                        </Typography>
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: 10,
                                                                                color: theme[themeStyle].tag_white,
                                                                            }}>
                                                                            {prize?.prize}
                                                                        </Typography>
                                                                    </Box>
                                                                    {!isPreloadedAward(
                                                                        submission.project._id,
                                                                        prize?._id || '',
                                                                    ) && (
                                                                        <IconButton
                                                                            onClick={() =>
                                                                                removeAward(
                                                                                    submission.project._id,
                                                                                    prize?._id,
                                                                                )
                                                                            }
                                                                            sx={{
                                                                                color: theme[themeStyle].tag_white,
                                                                                '&:hover': {
                                                                                    color: theme[themeStyle]
                                                                                        .primary_orange,
                                                                                },
                                                                            }}>
                                                                            <Close />
                                                                        </IconButton>
                                                                    )}
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Box>

                                                {isDropping && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            inset: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: `${theme[themeStyle].primary_orange}20`,
                                                            borderRadius: 2,
                                                            pointerEvents: 'none',
                                                        }}>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 14,
                                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                color: theme[themeStyle].primary_orange,
                                                            }}>
                                                            Drop to award prize
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Footer */}
                    <Box
                        sx={{
                            p: 3,
                            borderTop: `1px solid ${theme[themeStyle].placeholderGray50}`,
                            backgroundColor: theme[themeStyle].secondaryBg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                        <Typography
                            sx={{
                                fontSize: 14,
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                            }}>
                            {awards.length} {awards.length === 1 ? 'prize' : 'prizes'} awarded
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <CustomButton
                                onClick={onClose}
                                label="Cancel"
                                customStyle={{
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${theme[themeStyle].placeholderGray50}`,
                                    color: theme[themeStyle].primary_white,
                                }}
                            />
                            <CustomButton
                                onClick={handleSubmit}
                                disabled={awards.length === 0}
                                label="Confirm Awards"
                                customStyle={{
                                    backgroundColor: theme[themeStyle].primary_orange,
                                    color: theme[themeStyle].black,
                                }}
                            />
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Confirmation Dialog */}
            <DialogueSheet
                open={confirmDialogOpen}
                onClose={handleCancelConfirmation}
                onAgreeClicked={handleConfirmedSubmit}
                onDisagreeClick={handleCancelConfirmation}
                positiveText="Yes, Confirm Awards"
                negativeText="Cancel"
                header="Confirm Award Assignment"
                body="Are you sure you want to assign these awards? This action cannot be reversed and will permanently assign the selected prizes to the submissions."
            />
        </>
    );
};
